import type { TFigureRef, TTableBlock } from "@/zapi-frontend/shared/types/contentBlocks";
import { BlockLabel } from "./_BlockLabel";
import { BlockCaption } from "./_BlockCaption";
import { BlockTable } from "./_BlockTable";
import { refForCell } from "@/zapi-frontend/shared/lib/figureRefs";
import { RecordChip } from "./_RecordChip";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import { memoType } from "./memoTypography";
import { cn } from "@/zapi-frontend/shared/lib/utils";

// The natural form of a table, and the fallback for a chart that cannot draw: every row and
// every cell as stored, so nothing the run wrote is out of the reader's reach.
export function TableBlock({ block }: { block: TTableBlock }) {
  const render = block.render;
  const badgeCols = render?.kind === "records" ? render.badgeCols ?? [] : [];
  const isList = render?.kind === "records" && render.layout === "list";

  return (
    <figure className="flex min-w-0 flex-col gap-3">
      <BlockLabel label={block.label} className="" />
      {isList ? (
        <RecordList cols={block.cols} rows={block.rows} badgeCols={badgeCols} refs={block.refs} />
      ) : (
        <BlockTable
          cols={block.cols}
          rows={block.rows}
          badgeCols={badgeCols}
          hideFirstHeader={render?.kind === "grid"}
          refs={block.refs}
        />
      )}
      <BlockCaption caption={render?.note ?? null} className="" />
    </figure>
  );
}

// One record after another: the first column titles it, badge columns become chips beside
// the title, every other column is a label/value line.
function RecordList({
  cols,
  rows,
  badgeCols,
  refs
}: {
  cols: string[];
  rows: string[][];
  badgeCols: number[];
  refs?: TFigureRef[];
}) {
  const isBadge = cols.map((_col, c) => c > 0 && badgeCols.includes(c));

  return (
    <div data-testid="record-list">
      {rows.map((row, r) => (
        <div key={r} className="flex flex-col gap-2 border-b border-innoveraZinc-150 px-3 py-4 last:border-b-0">
          <div className="flex items-start justify-between gap-3">
            <span className={memoType.recordTitle}>{row[0]}</span>
            <span className="flex shrink-0 flex-wrap justify-end gap-1.5">
              {cols.map((_col, c) => (isBadge[c] && row[c] ? <RecordChip key={c} value={row[c]} /> : null))}
            </span>
          </div>
          {cols.map((col, c) =>
            c === 0 || isBadge[c] ? null : (
              <div key={c} className="flex gap-4">
                <div className={cn("w-[140px] shrink-0 pt-[3px]", memoType.overline)}>{col}</div>
                <div className={cn("min-w-0 flex-1", memoType.recordValue)}>
                  <FigureTrigger figureKey={refForCell(refs, r, c)?.key}>{row[c] || "—"}</FigureTrigger>
                </div>
              </div>
            )
          )}
        </div>
      ))}
    </div>
  );
}
